import { memo, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Button, Flex, Heading, VStack, Menu, MenuButton, IconButton, useColorModeValue } from '@chakra-ui/react';
import { ColumnProps, ColumnPresentationProps } from './types';
import { colors } from './constants';
import { LuMoreVertical, LuGripVertical, LuPlus } from 'react-icons/lu';
import ColumnActions from './column-actions';

const ColumnPresentation = memo(({
  title,
  children,
  isDragging,
  isDraggable,
  hasNewButton,
  listeners,
  onAddItem,
  style,
  colorScheme,
  onEdit,
  onDelete,
}: ColumnPresentationProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Box
      style={style}
      w="438px"
      h="calc(100vh - 144px)"
      overflow="hidden"
      p={4}
      bg={colorScheme.background}
      borderRadius="xl"
      opacity={isDragging ? 0.5 : 1}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <VStack spacing={4} align="stretch" maxH="full">
        <Flex align="center" justify="space-between">
          <Flex role="button" cursor={isDraggable ? "grab" : "default"} gap={1} align="center" color={colorScheme.tagText} bg={colorScheme.tagBackground} py={1} px={2} borderRadius="md" {...listeners}>
            {isDraggable && isHovered && (
              <LuGripVertical size={12} />
            )}
            <Heading fontSize={12} >{title}</Heading>
          </Flex>

          {(hasNewButton || isDraggable) && (
            <Flex align="center">
              {(hasNewButton || isDraggable) && <IconButton
                aria-label="Add Item"
                size="sm"
                color={colorScheme.tagText}
                bg="transparent"
                _hover={{
                  bg: colorScheme.tagBackground
                }}
                onClick={onAddItem}
              >
                <LuPlus />
              </IconButton>}
              {isDraggable && <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<LuMoreVertical />}
                  size="sm"
                  color={colorScheme.tagText}
                  bg="transparent"
                  _hover={{
                    bg: colorScheme.tagBackground
                  }}
                />
                <ColumnActions
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              </Menu>}
            </Flex>
          )}
        </Flex>

        {children}
        {onAddItem && (
          <Button
            variant="outline"
            onClick={onAddItem}
            color={colorScheme.placeholderText}
            borderColor={colorScheme.tagBackground}
            _hover={{
              bg: colorScheme.tagBackground,
              color: colorScheme.tagText
            }}
            display="flex"
            alignItems="center"
            gap={2}
            fontSize="sm"
            flexShrink={0}
          >
            <LuPlus />
            <span>Write a post</span>
          </Button>
        )}
      </VStack>
    </Box>
  );
});

export const Column = memo(({
  id,
  children,
  title = 'New Column',
  onAddItem,
  color = 'grey',
  isDraggable = true,
  hasNewButton = false,
  onEdit,
  onDelete,
}: ColumnProps) => {
  const {
    attributes,
    setNodeRef,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: id,
    data: {
      type: 'column',
    },
    disabled: !isDraggable,
  });

  const colorScheme = useColorModeValue(colors.light[color], colors.dark[color]);

  return (
    <Box ref={setNodeRef} {...attributes} h="full">
      <ColumnPresentation
        title={title}
        children={children}
        isDragging={isDragging}
        isDraggable={isDraggable}
        hasNewButton={hasNewButton}
        listeners={listeners}
        onAddItem={onAddItem}
        colorScheme={colorScheme}
        style={{
          transition,
          transform: CSS.Translate.toString(transform),
        }}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </Box>
  );
}); 